import { useMemo } from 'react';
import {
  getGetOralExamRunStateQueryKey,
  useGetOralExamRunState,
} from '@/common/api/endpoints/oral-exams/oral-exams';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import { isOralExamRunStateTerminal } from '@/common/utils/oralExam/runConduct';

export const ORAL_EXAM_SAFETY_POLL_MS = 5000;

export type OralExamRouteIds = {
  courseId: string;
  moduleId: string;
  taskId: string;
  bookingId: string;
};

export function unwrapOralExamRunState(data: unknown): OralExamRunStateResponse | null {
  if (!data || typeof data !== 'object') return null;
  const layer = data as { data?: { data?: OralExamRunStateResponse } };
  return layer.data?.data ?? null;
}

/**
 * Poll while the exam session is active. Stop when the student's run is terminal
 * (completed/aborted) or the cohort has moved to post_exam — do not keep
 * polling until the lecturer closes the cohort.
 */
export function oralExamRunStateRefetchIntervalMs(
  runState: OralExamRunStateResponse | null,
  polling: boolean,
): number | false {
  if (!polling) return false;
  if (runState?.sessionPhase === OralExamSessionPhase.post_exam) return false;
  if (isOralExamRunStateTerminal(runState?.state)) return false;
  return ORAL_EXAM_SAFETY_POLL_MS;
}

/**
 * Poll oral exam run state while the exam session is active.
 */
export function useOralExamRunStatePoll(
  ids: OralExamRouteIds,
  options: { enabled: boolean; polling: boolean },
) {
  const {
    courseId, moduleId, taskId, bookingId,
  } = ids;
  const baseEnabled = options.enabled
    && Boolean(courseId && moduleId && taskId && bookingId);

  const query = useGetOralExamRunState(courseId, moduleId, taskId, bookingId, {
    query: {
      enabled: baseEnabled,
      refetchInterval: (q) => oralExamRunStateRefetchIntervalMs(
        unwrapOralExamRunState(q.state.data),
        options.polling,
      ),
    },
  });

  const runState = useMemo(
    () => unwrapOralExamRunState(query.data),
    [query.data],
  );

  const queryKey = useMemo(
    () => getGetOralExamRunStateQueryKey(courseId, moduleId, taskId, bookingId),
    [bookingId, courseId, moduleId, taskId],
  );

  return {
    runState,
    etag: runState?.etag ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
    queryKey,
  };
}
